/**
 * Organizations Page
 *
 * List of all organizations with metrics.
 * Click to drill down into individual org details.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import DataTable from '../../components/founder-dashboard/DataTable';
import HealthBadge from '../../components/founder-dashboard/HealthBadge';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';

export default function OrganizationsPage() {
  const { currentEnv } = useEnvironment();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOrganizations({ limit: 100 });
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OrganizationsPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
  }, [currentEnv]);

  const columns = [
    {
      key: 'name',
      header: 'Organization',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{row.slug}</p>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === 'enterprise'
              ? 'bg-purple-100 text-purple-800'
              : value === 'pro'
              ? 'bg-blue-100 text-blue-800'
              : value === 'starter'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {value || 'Trial'}
        </span>
      ),
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
    },
    {
      key: 'groupCount',
      header: 'Groups',
      sortable: true,
    },
    {
      key: 'mrr',
      header: 'MRR',
      sortable: true,
      render: (value) =>
        value
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(value)
          : '$0',
    },
    {
      key: 'healthScore',
      header: 'Health',
      sortable: true,
      render: (value) => <HealthBadge score={value} showLabel={false} />,
    },
    {
      key: 'lastActivityAt',
      header: 'Last Active',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-gray-400">Never</span>;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const d = new Date(value);
        const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return <span className="text-green-600">Today</span>;
        if (diffDays === 1) return <span className="text-green-600">Yesterday</span>;
        if (diffDays < 7) return <span className="text-yellow-600">{diffDays}d ago</span>;
        if (diffDays < 30) return <span className="text-orange-600">{Math.floor(diffDays / 7)}w ago</span>;
        return <span className="text-red-600">{Math.floor(diffDays / 30)}mo ago</span>;
      },
    },
  ];

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading organizations</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Organizations ({data?.total || 0})
          </h2>
          <p className="text-gray-500 mt-1">
            Click on any organization to view detailed metrics
          </p>
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Data Table */}
      <DataTable
        data={data?.organizations || []}
        columns={columns}
        loading={loading}
        onRowClick={(org) => navigate(`/founder-dashboard/organizations/${org.id}`)}
        emptyMessage="No organizations found"
      />
    </div>
  );
}
